import type { Hex } from 'viem';
import { sendTransaction, waitForTransactionReceipt } from 'wagmi/actions';

import type { walletWagmiConfig } from '@/features/wallet/config/appkitConfig';
// deep import on purpose: the wallet barrel reaches back into this feature
import { ensureWalletChain } from '@/features/wallet/services/walletChain';
import { DEFAULT_ORIGIN_CHAIN_ID } from '@/lib/api/speed';
import type { RelayStep } from '@/lib/api/speed';

type WagmiConfig = typeof walletWagmiConfig;

/** Coerce a Relay-provided value/gas field (string | number | undefined) to bigint. */
function toBigInt(value: string | number | undefined | null): bigint {
    if (value === undefined || value === null || value === '') return 0n;
    return BigInt(value);
}

export interface RelayStepProgress {
    /** 0-based index of the step currently being executed. */
    stepIndex: number;
    /** Total number of steps. */
    stepCount: number;
    /** Hash of the most recently submitted origin-chain transaction. */
    hash: Hex;
    /** Chain the transaction was submitted on. */
    chainId: number;
}

/**
 * Execute the ordered Relay steps returned by `startDeposit` in
 * `mode: 'metamask'`.
 *
 * Each step contains one or more items; every item with a non-null `data`
 * payload becomes a wallet transaction submitted on the chain the step names
 * (Cronos when it names none). Steps run strictly in order and each transaction
 * is mined before the next is submitted. The user signs each transaction in
 * their wallet.
 *
 * The wallet is switched to the step's chain first, explicitly. wagmi's
 * `sendTransaction({ chainId })` only ASSERTS the chain for injected wallets, so
 * a wallet sitting on Cronos handed a Robinhood step failed before MetaMask ever
 * prompted, on every Continue, with nothing on chain to show for it (deposit job
 * 01M1F4ENBFA1ZQ2CJFR0KSH7JD, 2026-09-01). Deposit sources span both anchors now,
 * so the connected chain and the source chain routinely differ.
 *
 * Returns the hashes of all submitted transactions. Throws on the first
 * rejection / revert (the caller maps the error to a friendly message).
 */
export async function executeRelaySteps(
    config: WagmiConfig,
    steps: RelayStep[],
    onProgress?: (progress: RelayStepProgress) => void | Promise<void>,
): Promise<Hex[]> {
    const hashes: Hex[] = [];

    for (let stepIndex = 0; stepIndex < steps.length; stepIndex += 1) {
        const step = steps[stepIndex];

        for (const item of step.items) {
            const data = item.data;
            if (!data) continue;

            const chainId = data.chainId ?? DEFAULT_ORIGIN_CHAIN_ID;
            await ensureWalletChain(config, chainId);

            const hash = await sendTransaction(config, {
                to: data.to as `0x${string}`,
                data: (data.data ?? '0x') as `0x${string}`,
                value: toBigInt(data.value),
                chainId,
            });

            hashes.push(hash);
            await onProgress?.({
                stepIndex,
                stepCount: steps.length,
                hash,
                chainId,
            });

            await waitForTransactionReceipt(config, { hash, chainId });
        }
    }

    if (hashes.length === 0) {
        throw new Error('Deposit signature step is missing.');
    }

    return hashes;
}
